import { useFormContext } from "react-hook-form";
import type { OrganizationFeature } from "@/types/organization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DATE_FORMAT_OPTIONS,
  FEATURE_OPTIONS,
  SSO_MODE_OPTIONS,
  TIME_FORMAT_OPTIONS,
  TWO_FACTOR_CHANNEL_OPTIONS,
} from "@/components/modals/organizationSettings/constants";
import type { OrganizationFormValues } from "@/components/modals/organizationSettings/types";

interface Props {
  onSubmit: (data: OrganizationFormValues) => Promise<void>;
  onCancel: () => void;
  isUpdating: boolean;
}

export const GeneralTab = ({ onSubmit, onCancel, isUpdating }: Props) => {
  const { register, handleSubmit, watch, setValue } = useFormContext<OrganizationFormValues>();

  const selectedFeatures = watch("features") || [];
  const enabled2fa = watch("enabled2fa");

  const handleFeatureToggle = (feature: OrganizationFeature) => {
    const isSelected = selectedFeatures.includes(feature);
    const newFeatures = isSelected ? selectedFeatures.filter(f => f !== feature) : [...selectedFeatures, feature];
    setValue("features", newFeatures);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name">Organization Name</Label>
        <Input id="name" {...register("name", { required: true })} placeholder="Enter organization name" />
      </div>

      <div className="space-y-2 border-t pt-6">
        <Label htmlFor="contract-family-divisions">Contract Family divisions</Label>
        <textarea
          id="contract-family-divisions"
          rows={4}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder="One division per line"
          {...register("contractFamilyDivisionsText")}
        />
        <p className="text-xs text-gray-500">Enter one division per line. These choices appear on Contract Families.</p>
      </div>

      <div className="space-y-3">
        <Label>Features</Label>
        <div className="grid grid-cols-2 gap-2">
          {FEATURE_OPTIONS.map(feature => (
            <label
              key={feature.value}
              className="flex cursor-pointer items-center space-x-3 rounded-md border p-3 transition-colors hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={selectedFeatures.includes(feature.value)}
                onChange={() => handleFeatureToggle(feature.value)}
                className="text-atriablue-500 focus:ring-atriablue-500 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm font-medium">{feature.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <label className="flex cursor-pointer items-center space-x-3 rounded-md border p-3 transition-colors hover:bg-gray-50">
          <input
            type="checkbox"
            {...register("enabled2fa")}
            className="text-atriablue-500 focus:ring-atriablue-500 h-4 w-4 rounded border-gray-300"
          />
          <div>
            <span className="text-sm font-medium">Enable Two-Factor Authentication</span>
            <p className="text-xs text-gray-500">Require 2FA for all users in this organization</p>
          </div>
        </label>
        <div className="space-y-2 pl-7">
          <Label className="text-sm font-normal text-gray-500">Primary 2FA channel</Label>
          {TWO_FACTOR_CHANNEL_OPTIONS.map(option => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center space-x-3 rounded-md border p-3 transition-colors hover:bg-gray-50"
            >
              <input
                type="radio"
                value={option.value}
                {...register("twoFactorPrimaryChannel")}
                disabled={!enabled2fa}
                className="text-atriablue-500 focus:ring-atriablue-500 h-4 w-4 border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <div>
                <span className="text-sm font-medium">{option.label}</span>
                <p className="text-xs text-gray-500">{option.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3 border-t pt-6">
        <Label>Date Format</Label>
        <div className="space-y-2">
          {DATE_FORMAT_OPTIONS.map(option => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center space-x-3 rounded-md border p-3 transition-colors hover:bg-gray-50"
            >
              <input
                type="radio"
                value={option.value}
                {...register("dateFormat")}
                className="text-atriablue-500 focus:ring-atriablue-500 h-4 w-4 border-gray-300"
              />
              <span className="text-sm font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <Label>Time Format</Label>
        <div className="space-y-2">
          {TIME_FORMAT_OPTIONS.map(option => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center space-x-3 rounded-md border p-3 transition-colors hover:bg-gray-50"
            >
              <input
                type="radio"
                value={option.value}
                {...register("timeFormat")}
                className="text-atriablue-500 focus:ring-atriablue-500 h-4 w-4 border-gray-300"
              />
              <span className="text-sm font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3 border-t pt-6">
        <Label>SSO Configuration</Label>

        <div className="space-y-2">
          <Label htmlFor="ssoDomainsText" className="text-sm font-normal text-gray-500">
            Email Domains
          </Label>
          <Input id="ssoDomainsText" {...register("ssoDomainsText")} placeholder="contoso.com, contoso.co.uk" />
          <p className="text-xs text-gray-500">
            Separate multiple domains with commas. Each domain can belong to only one organization.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-normal text-gray-500">SSO Mode</Label>
          {SSO_MODE_OPTIONS.map(option => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center space-x-3 rounded-md border p-3 transition-colors hover:bg-gray-50"
            >
              <input
                type="radio"
                value={option.value}
                {...register("ssoMode")}
                className="text-atriablue-500 focus:ring-atriablue-500 h-4 w-4 border-gray-300"
              />
              <div>
                <span className="text-sm font-medium">{option.label}</span>
                <p className="text-xs text-gray-500">{option.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t pt-6">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isUpdating}>
          Cancel
        </Button>
        <Button type="submit" disabled={isUpdating}>
          {isUpdating ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </form>
  );
};
